import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  autoUpdate,
  flip,
  Placement,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { IconWrapper, SvgIcon } from '../Icon';
import CloseOverlayButton from '../CloseOverlayButton';
import {
  EntityInfoPopoverContext,
  IEntityInfoPopoverContext,
} from './EntityInfoPopoverContext';
import { floatingComponentStyles } from '../../styles/shareableStyles';
import { FloatingLayer } from '../FloatingManager';

interface IProps {
  children: ReactNode;
  containerSize?: number;
  iconSize?: number;
  onOpen?: () => void;
  onClose?: () => void;
  closeOnAncestorScroll?: boolean;
  placement?: Placement | undefined;
}

const Container = styled.div`
  position: relative;
`;
const Wrapper = styled.div`
  ${floatingComponentStyles};
`;
const StyledCloseOverlayButton = styled(CloseOverlayButton)`
  position: absolute;
  top: 0;
  right: -30px;
`;

const EntityInfoPopover = ({
  children,
  containerSize = 24,
  iconSize = 16,
  onOpen,
  onClose,
  closeOnAncestorScroll,
  placement,
}: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { floatingStyles, refs, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
      if (open) {
        onOpen?.();
      } else {
        onClose?.();
      }
    },
    middleware: [flip()],
    placement: placement || 'bottom-start',
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, {
    ancestorScroll: closeOnAncestorScroll,
  });
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const handleClick = useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();
  }, []);

  const handleClose = useCallback(
    (event: React.SyntheticEvent) => {
      event.stopPropagation();
      event.preventDefault();
      setIsOpen(false);
      onClose?.();
    },
    [onClose],
  );

  const contextValue = useMemo<IEntityInfoPopoverContext>(
    () => ({
      closePopover: handleClose,
    }),
    [handleClose],
  );

  return (
    <>
      <IconWrapper
        ref={refs.setReference}
        size={containerSize}
        {...getReferenceProps({
          onClick: handleClick,
        })}
      >
        <SvgIcon
          name="info"
          width={iconSize}
          height={iconSize}
          isActive={isOpen}
        />
      </IconWrapper>
      {isOpen && (
        <FloatingLayer>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <Container onClick={(event) => event.stopPropagation()}>
              <Wrapper>
                <EntityInfoPopoverContext.Provider value={contextValue}>
                  {children}
                </EntityInfoPopoverContext.Provider>
              </Wrapper>
              <StyledCloseOverlayButton onClick={handleClose} />
            </Container>
          </div>
        </FloatingLayer>
      )}
    </>
  );
};

export default EntityInfoPopover;
